import React, { useState, useEffect } from 'react';
import { 
  SearchIcon,
  ChatAlt2Icon,
  PaperAirplaneIcon,
  UserCircleIcon,
  ClockIcon,
  CheckIcon
} from '@heroicons/react/outline';
import { useAuth } from '../context/AuthContext';
import { useLocation } from 'react-router-dom';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

const Chat = () => {
  const { user, userProfile } = useAuth();
  const location = useLocation();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Handle conversation selection from navigation state
  useEffect(() => {
    if (location.state?.selectedConversationId && conversations.length > 0) {
      const conversation = conversations.find(conv => conv.id === location.state.selectedConversationId);
      if (conversation) {
        setSelectedConversation(conversation);
        // Clear the navigation state to prevent re-selection on re-render
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, conversations]);

  // Fetch user's conversations
  useEffect(() => {
    if (!user) return;

    const conversationsRef = collection(db, 'conversations');
    const q = query(
      conversationsRef,
      where('participants', 'array-contains', user.uid),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const conversationList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setConversations(conversationList);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }

    const messagesRef = collection(db, 'messages');
    const q = query(
      messagesRef,
      where('conversationId', '==', selectedConversation.id),
      orderBy('createdAt', 'asc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const messageList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      setMessages(messageList);
    });

    return () => unsubscribe();
  }, [selectedConversation]);

  const filteredConversations = conversations.filter(conv => {
    const otherParticipant = conv.participants.find(p => p.uid !== user?.uid);
    return otherParticipant?.name?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      const messageData = {
        conversationId: selectedConversation.id,
        text: message.trim(),
        sender: user.uid,
        receiver: selectedConversation.participants.find(p => p.uid !== user.uid)?.uid,
        createdAt: serverTimestamp(),
        user: {
          _id: user.uid,
          name: userProfile?.fullName || user?.displayName || 'User',
          avatar: userProfile?.photoUrl || null,
        },
        sent: true,
        received: true
      };

      await addDoc(collection(db, 'messages'), messageData);

      // Update conversation with last message
      await updateDoc(doc(db, 'conversations', selectedConversation.id), {
        lastMessage: message.trim(),
        lastMessageAuthor: userProfile?.fullName || user?.displayName || 'User',
        lastMessageAuthorPhotoUrl: userProfile?.photoUrl || null,
        updatedAt: serverTimestamp()
      });

      setMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const now = new Date();
    const messageTime = timestamp instanceof Date ? timestamp : timestamp.toDate();
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} min ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hour${Math.floor(diffInMinutes / 60) > 1 ? 's' : ''} ago`;
    return messageTime.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-CC5500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <h1 className="text-2xl font-bold text-gray-900">
              Messages
            </h1>
            <p className="text-gray-600">
              Communicate with suppliers and buyers
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="flex h-96">
            {/* Conversations List */}
            <div className="w-1/3 border-r border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search conversations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-CC5500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="overflow-y-auto h-full">
                {filteredConversations.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <ChatAlt2Icon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  filteredConversations.map((conversation) => {
                    const otherParticipant = conversation.participants.find(p => p.uid !== user?.uid);
                    return (
                  <div
                    key={conversation.id}
                    onClick={() => setSelectedConversation(conversation)}
                    className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedConversation?.id === conversation.id ? 'bg-orange-50 border-r-2 border-r-CC5500' : ''
                    }`}
                  >
                    <div className="flex items-center">
                      <div className="relative">
                            {otherParticipant?.photoUrl ? (
                          <img
                                src={otherParticipant.photoUrl}
                                alt={otherParticipant.name}
                            className="w-12 h-12 rounded-full"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                            <UserCircleIcon className="h-6 w-6 text-gray-500" />
                          </div>
                        )}
                            {otherParticipant?.online && (
                          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      
                      <div className="ml-3 flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                                {otherParticipant?.name || 'Unknown User'}
                          </h3>
                          <span className="text-xs text-gray-500">
                                {formatTime(conversation.updatedAt)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 truncate">
                              {conversation.lastMessage || 'No messages yet'}
                        </p>
                      </div>

                          {conversation.unreadCount > 0 && (
                        <div className="ml-2 bg-CC5500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                              {conversation.unreadCount}
                        </div>
                      )}
                    </div>
                  </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 flex flex-col">
              {selectedConversation ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200">
                    <div className="flex items-center">
                      {(() => {
                        const otherParticipant = selectedConversation.participants.find(p => p.uid !== user?.uid);
                        return (
                          <>
                      <div className="relative">
                              {otherParticipant?.photoUrl ? (
                          <img
                                  src={otherParticipant.photoUrl}
                                  alt={otherParticipant.name}
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                            <UserCircleIcon className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                              {otherParticipant?.online && (
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                        )}
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-gray-900">
                                {otherParticipant?.name || 'Unknown User'}
                        </h3>
                        <p className="text-xs text-gray-500">
                                {otherParticipant?.online ? 'Online' : 'Offline'}
                        </p>
                      </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4">
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        <ChatAlt2Icon className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p>No messages yet</p>
                        <p className="text-sm">Start the conversation!</p>
                      </div>
                    ) : (
                      messages.map((msg) => {
                        const isOwnMessage = msg.sender === user?.uid;
                        return (
                      <div
                        key={msg.id}
                            className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs px-4 py-2 rounded-2xl ${
                                isOwnMessage
                              ? 'bg-CC5500 text-white'
                              : 'bg-gray-100 text-gray-900'
                          }`}
                        >
                          <p className="text-sm">{msg.text}</p>
                          <div className={`flex items-center justify-between mt-1 ${
                                isOwnMessage ? 'text-orange-100' : 'text-gray-500'
                          }`}>
                                <span className="text-xs">{formatTime(msg.createdAt)}</span>
                                {isOwnMessage && (
                              <span className="ml-2">
                                    {msg.received ? (
                                  <CheckIcon className="h-3 w-3" />
                                ) : (
                                  <ClockIcon className="h-3 w-3" />
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                        );
                      })
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200">
                    <form onSubmit={sendMessage} className="flex space-x-2">
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-CC5500 focus:border-transparent"
                        disabled={sending}
                      />
                      <button
                        type="submit"
                        disabled={!message.trim() || sending}
                        className="p-2 bg-CC5500 text-white rounded-full hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {sending ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                        <PaperAirplaneIcon className="h-5 w-5" />
                        )}
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <ChatAlt2Icon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Select a conversation
                    </h3>
                    <p className="text-gray-500">
                      Choose a conversation from the list to start messaging
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat; 